import React, { useMemo, useState } from 'react';
import { useModal } from '../contexts/ModalContext';
import { useJobsData, useUpdateJobStatus } from '../hooks/useData';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Search as SearchIcon, AlertTriangle, UserX, CheckCircle, Calendar as CalendarIcon } from 'lucide-react';
import EmptyState from '../components/EmptyState';

// ───────────────────────── Helpers ─────────────────────────
const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const addDays = (date: Date, days: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const startOfWeek = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

const formatDate = (d: string | Date) => {
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};
const formatTime = (d: string | Date) => {
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

// ───────────────────────── Status + palette ─────────────────────────
const STATUS_META: Record<string, { label: string; color: string; bg: string; border: string }> = {
  PENDING: { label: 'Pending', color: '#6B7280', bg: '#F3F4F6', border: '#E5E7EB' },
  SCHEDULED: { label: 'Scheduled', color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE' },
  ASSIGNED: { label: 'Assigned', color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
  EN_ROUTE: { label: 'En route', color: '#0891B2', bg: '#ECFEFF', border: '#A5F3FC' },
  ON_SITE: { label: 'On site', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
  IN_PROGRESS: { label: 'In progress', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  COMPLETED: { label: 'Completed', color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0' },
  PENDING_VALIDATION: { label: 'Needs validation', color: '#EA580C', bg: '#FFF7ED', border: '#FED7AA' },
  CANCELLED: { label: 'Cancelled', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
  INVOICED: { label: 'Invoiced', color: '#0F766E', bg: '#F0FDFA', border: '#99F6E4' },
};

const BLOCK_PALETTES = [
  { bg: '#E5F7EE', border: '#A7E5C3', text: '#0E6E3F' },
  { bg: '#EEF1FF', border: '#B7C3FF', text: '#3A3FAE' },
  { bg: '#FDECEC', border: '#F7B9B9', text: '#B42D2D' },
  { bg: '#F3EDFE', border: '#D3BFF6', text: '#6B3FAC' },
  { bg: '#FFF6E0', border: '#F4D98A', text: '#8F6200' },
  { bg: '#E7F5FB', border: '#9ED5EE', text: '#0F5E8A' },
];
const paletteForKey = (key: string) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return BLOCK_PALETTES[hash % BLOCK_PALETTES.length];
};

function StatusBadge({ status }: { status: string }) {
  const m = STATUS_META[status] || STATUS_META.PENDING;
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium leading-tight"
      style={{ color: m.color, background: m.bg, borderColor: m.border }}
    >
      {m.label}
    </span>
  );
}

function RiskBadge({ flags }: { flags?: { isOverdue?: boolean; isUnassigned?: boolean; isMissingInfo?: boolean } }) {
  if (!flags) return null;
  if (flags.isOverdue)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-[10px] font-medium text-red-700">
        <AlertTriangle className="h-3 w-3" /> Overdue
      </span>
    );
  if (flags.isUnassigned)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-medium text-amber-700">
        <UserX className="h-3 w-3" /> Unassigned
      </span>
    );
  if (flags.isMissingInfo)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 border border-orange-200 px-2 py-0.5 text-[10px] font-medium text-orange-700">
        <AlertTriangle className="h-3 w-3" /> Missing info
      </span>
    );
  return null;
}

// ───────────────────────── Slide-over ─────────────────────────
function JobSlideOver({
  job,
  onClose,
  onStatusChange,
}: {
  job: any;
  onClose: () => void;
  onStatusChange: (id: string, status: string) => void;
}) {
  const nextStatuses: Record<string, string[]> = {
    PENDING: ['SCHEDULED', 'CANCELLED'],
    SCHEDULED: ['ASSIGNED', 'CANCELLED'],
    ASSIGNED: ['EN_ROUTE', 'CANCELLED'],
    EN_ROUTE: ['ON_SITE'],
    ON_SITE: ['IN_PROGRESS'],
    IN_PROGRESS: ['COMPLETED', 'PENDING_VALIDATION'],
    COMPLETED: ['INVOICED', 'PENDING_VALIDATION'],
    PENDING_VALIDATION: ['COMPLETED', 'INVOICED'],
    INVOICED: [],
    CANCELLED: [],
  };
  const next = nextStatuses[job.status] || [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/20" onClick={onClose}>
      <div
        className="h-full w-full max-w-md bg-white shadow-2xl flex flex-col overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 truncate">
              {job.title || `JOB-${String(job.id).slice(0, 8)}`}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {job.client?.name || '—'}
              {job.site?.name ? ` · ${job.site.name}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-3 flex-shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 px-5 py-4 space-y-5 text-sm">
          <div>
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2">Status</p>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={job.status} />
              {next.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onStatusChange(job.id, s)}
                  className="inline-flex items-center rounded-md bg-[#181818] px-2.5 py-1 text-[11px] font-medium text-white hover:bg-[#2d2d2d]"
                >
                  → {STATUS_META[s]?.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2">Schedule</p>
            <p className="text-sm text-gray-900">
              {job.scheduledDate
                ? `${formatDate(job.scheduledDate)} · ${formatTime(job.scheduledDate)}`
                : 'Not scheduled'}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2">Engineer</p>
            <p className="text-sm text-gray-900">{job.assignedEngineer?.name || 'Unassigned'}</p>
          </div>
          <div>
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2">Client</p>
            <p className="text-sm text-gray-900">{job.client?.name || '—'}</p>
            {job.client?.phone && <p className="text-xs text-gray-500">{job.client.phone}</p>}
            {job.client?.email && <p className="text-xs text-gray-500">{job.client.email}</p>}
          </div>
          {(job.address || job.postcode || job.site?.address) && (
            <div>
              <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2">Site</p>
              {job.site?.name && <p className="text-sm font-medium text-gray-900">{job.site.name}</p>}
              <p className="text-sm text-gray-700">{job.address || job.site?.address || ''}</p>
              {job.postcode && <p className="text-xs text-gray-500">{job.postcode}</p>}
            </div>
          )}
          {job.description && (
            <div>
              <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2">Description</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{job.description}</p>
            </div>
          )}
          <RiskBadge flags={job.riskFlags} />
        </div>
      </div>
    </div>
  );
}

// ───────────────────────── Main page ─────────────────────────
type ViewRange = 'day' | 'week' | 'month' | 'staff';

export default function Jobs() {
  const { openModal } = useModal();
  const { data: jobsData, isLoading: loading, error } = useJobsData();
  const updateStatus = useUpdateJobStatus();

  const allJobs: any[] = useMemo(() => (jobsData as any)?.jobs || [], [jobsData]);
  const engineers: any[] = useMemo(() => (jobsData as any)?.engineers || [], [jobsData]);

  const [viewRange, setViewRange] = useState<ViewRange>('week');
  const [anchor, setAnchor] = useState<Date>(() => startOfWeek(new Date()));
  const [statusFilter, setStatusFilter] = useState('');
  const [engineerFilter, setEngineerFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJob, setSelectedJob] = useState<any | null>(null);
  const [rightTab, setRightTab] = useState<'needs_action' | 'upcoming' | 'completed'>('needs_action');

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  // Visible days depend on range
  const visibleDays = useMemo(() => {
    if (viewRange === 'day') return [anchor];
    if (viewRange === 'month') {
      const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const days: Date[] = [];
      const d = startOfWeek(first);
      for (let i = 0; i < 35; i++) days.push(addDays(d, i));
      return days;
    }
    // week / staff = 5 weekdays Mon–Fri
    return Array.from({ length: 5 }, (_, i) => addDays(anchor, i));
  }, [anchor, viewRange]);

  // ───────────── Filters ─────────────
  const filteredJobs = useMemo(() => {
    let filtered = [...allJobs];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (j) =>
          j.title?.toLowerCase().includes(q) ||
          j.client?.name?.toLowerCase().includes(q) ||
          j.assignedEngineer?.name?.toLowerCase().includes(q) ||
          j.address?.toLowerCase().includes(q),
      );
    }
    if (statusFilter) filtered = filtered.filter((j) => String(j.status || '').toUpperCase() === statusFilter);
    if (engineerFilter)
      filtered = filtered.filter((j) => (j.assignedEngineer?.name || '').toLowerCase() === engineerFilter.toLowerCase());
    return filtered;
  }, [allJobs, engineerFilter, searchTerm, statusFilter]);

  // ───────────── KPIs ─────────────
  const weekStart = useMemo(() => startOfWeek(new Date()), []);
  const weekEnd = useMemo(() => addDays(weekStart, 7), [weekStart]);
  const prevWeekStart = useMemo(() => addDays(weekStart, -7), [weekStart]);

  const kpis = useMemo(() => {
    const inRange = (d?: string | Date | null, from?: Date, to?: Date) => {
      if (!d || !from || !to) return false;
      const t = new Date(d).getTime();
      return t >= from.getTime() && t < to.getTime();
    };
    const thisWeek = allJobs.filter((j) => inRange(j.scheduledDate, weekStart, weekEnd));
    const lastWeek = allJobs.filter((j) => inRange(j.scheduledDate, prevWeekStart, weekStart));
    const completedThis = thisWeek.filter((j) =>
      ['COMPLETED', 'INVOICED', 'PENDING_VALIDATION'].includes(j.status),
    ).length;
    const completedLast = lastWeek.filter((j) =>
      ['COMPLETED', 'INVOICED', 'PENDING_VALIDATION'].includes(j.status),
    ).length;
    const unassigned = allJobs.filter((j) => !j.assignedEngineer?.id && j.status !== 'CANCELLED').length;

    const delta = (a: number, b: number) => (b === 0 ? (a === 0 ? 0 : 100) : Math.round(((a - b) / b) * 100));
    return {
      totalJobs: thisWeek.length,
      totalDelta: delta(thisWeek.length, lastWeek.length),
      completion: thisWeek.length === 0 ? 0 : Math.round((completedThis / thisWeek.length) * 100),
      completionDelta:
        lastWeek.length === 0
          ? 0
          : Math.round(
              ((thisWeek.length === 0 ? 0 : completedThis / thisWeek.length) -
                (lastWeek.length === 0 ? 0 : completedLast / lastWeek.length)) *
                100,
            ),
      unassigned,
    };
  }, [allJobs, prevWeekStart, weekEnd, weekStart]);

  // ───────────── Calendar time grid ─────────────
  const startHour = 8;
  const endHour = 18;
  const slotMinutes = 30;
  const slotHeight = 40;
  const slotCount = ((endHour - startHour) * 60) / slotMinutes;
  const gridStartMinutes = startHour * 60;
  const gridEndMinutes = gridStartMinutes + slotCount * slotMinutes;

  const timeLabels = useMemo(
    () =>
      Array.from({ length: slotCount + 1 }, (_, i) => {
        const mins = gridStartMinutes + i * slotMinutes;
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        const hh = h % 12 === 0 ? 12 : h % 12;
        const suffix = h < 12 ? 'AM' : 'PM';
        return { key: `t-${mins}`, mins, label: m === 0 ? `${String(hh).padStart(2, '0')}:00 ${suffix}` : '' };
      }),
    [gridStartMinutes, slotCount],
  );

  const jobsInGrid = useMemo(() => {
    return filteredJobs
      .filter((j) => j.scheduledDate)
      .map((j) => {
        const start = new Date(j.scheduledDate);
        const end = new Date(start.getTime() + ((j.duration as number) || 60) * 60 * 1000);
        return { job: j, start, end };
      });
  }, [filteredJobs]);

  // ───────────── Right-panel queues ─────────────
  const queues = useMemo(() => {
    const needs = filteredJobs.filter(
      (j) => j.riskFlags?.isOverdue || j.riskFlags?.isUnassigned || j.riskFlags?.isMissingInfo,
    );
    const upcoming = filteredJobs
      .filter(
        (j) =>
          ['PENDING', 'SCHEDULED', 'ASSIGNED'].includes(j.status) &&
          !needs.includes(j) &&
          (!j.scheduledDate || new Date(j.scheduledDate).getTime() >= today.getTime()),
      )
      .sort(
        (a, b) =>
          (new Date(a.scheduledDate || 0).getTime() || Infinity) -
          (new Date(b.scheduledDate || 0).getTime() || Infinity),
      );
    const completed = filteredJobs.filter((j) => ['COMPLETED', 'INVOICED'].includes(j.status));
    return { needs, upcoming, completed };
  }, [filteredJobs, today]);

  const handleStatusChange = async (id: string, status: string) => {
    await updateStatus.mutateAsync({ id, status });
    setSelectedJob((prev: any) => (prev?.id === id ? { ...prev, status } : prev));
  };

  const moveAnchor = (delta: number) => {
    if (viewRange === 'day') return setAnchor((p) => addDays(p, delta));
    if (viewRange === 'month') {
      return setAnchor((p) => {
        const n = new Date(p);
        n.setMonth(p.getMonth() + delta);
        return n;
      });
    }
    setAnchor((p) => addDays(p, delta * 7));
  };

  const anchorLabel = useMemo(() => {
    if (viewRange === 'day') return anchor.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    if (viewRange === 'month') return anchor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    const end = addDays(anchor, 4);
    return `${anchor.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }, [anchor, viewRange]);

  // ───────────── Loading / empty states ─────────────
  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">Jobs</h1>
            <p className="page-subtitle">Loading…</p>
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="page-container">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-900 mb-2">Error loading jobs</h3>
          <p className="text-red-700">{(error as any)?.message || 'Failed to load jobs'}</p>
        </div>
      </div>
    );
  }
  if (allJobs.length === 0) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">Jobs</h1>
            <p className="page-subtitle">Schedule and track every job in a single calendar.</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-primary" onClick={() => openModal('createJob')}>
              Create Job
            </button>
          </div>
        </div>
        <EmptyState
          icon={WrenchScrewdriverIcon}
          title="No jobs yet"
          description="Create your first job to start managing work orders."
          actionLabel="Create Job"
          onAction={() => openModal('createJob')}
        />
      </div>
    );
  }

  // ───────────── Render ─────────────
  return (
    <div className="page-container">
      {/* ─── Header row: title + date nav + view tabs + filters ─── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-[#191919]">Jobs</h1>
          <button
            type="button"
            onClick={() => setAnchor(startOfWeek(new Date()))}
            className="inline-flex h-8 items-center rounded-lg border border-[#D4D4D4] bg-white px-3 text-xs font-medium text-[#4B5563] hover:bg-[#F3F4F6]"
          >
            Today
          </button>
          <div className="flex items-center gap-1 px-2 text-sm text-[#374151]">
            <button
              type="button"
              onClick={() => moveAnchor(-1)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F3F4F6]"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <span className="px-2 font-medium">{anchorLabel}</span>
            <button
              type="button"
              onClick={() => moveAnchor(1)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F3F4F6]"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex h-9 rounded-lg border border-[#D4D4D4] bg-white p-1">
            {(['day', 'week', 'month', 'staff'] as ViewRange[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setViewRange(r)}
                className={`inline-flex h-full items-center justify-center rounded-md px-3 text-xs font-medium transition-colors ${
                  viewRange === r ? 'bg-[#F3F4F6] text-[#111827]' : 'text-[#4B5563] hover:bg-[#F3F4F6]'
                }`}
              >
                {r[0].toUpperCase() + r.slice(1)}
              </button>
            ))}
          </div>
          <select
            className="h-9 rounded-lg border border-[#D4D4D4] bg-white px-3 text-xs font-medium text-[#4B5563] hover:bg-[#F3F4F6]"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All status</option>
            {Object.entries(STATUS_META).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
          <select
            className="h-9 rounded-lg border border-[#D4D4D4] bg-white px-3 text-xs font-medium text-[#4B5563] hover:bg-[#F3F4F6]"
            value={engineerFilter}
            onChange={(e) => setEngineerFilter(e.target.value)}
          >
            <option value="">All staff</option>
            {engineers.map((eng) => (
              <option key={eng.id || eng.name} value={eng.name}>
                {eng.name}
              </option>
            ))}
          </select>
          <div className="relative w-48">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#686E6C]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search jobs…"
              className="h-9 w-full rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] pl-9 pr-3 text-sm text-[#383838] placeholder:text-[#686E6C] focus:outline-none focus:ring-2 focus:ring-[#CDCDCD]/80"
            />
          </div>
          <button
            type="button"
            onClick={() => openModal('createJob')}
            className="inline-flex h-9 items-center justify-center rounded-lg bg-[#181818] px-3 text-xs font-medium text-white hover:bg-[#2d2d2d]"
          >
            + New
          </button>
        </div>
      </div>

      {/* ─── Split: Calendar (left) + Attention panel (right) ─── */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* LEFT: KPIs + Calendar */}
        <div className="space-y-4">
          {/* KPI row */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KpiCard
              title="Total jobs"
              subtitle="Last 7 days"
              value={String(kpis.totalJobs)}
              delta={kpis.totalDelta}
              good={kpis.totalDelta >= 0}
            />
            <KpiCard
              title="Completion rate"
              subtitle="Last 7 days"
              value={`${kpis.completion}%`}
              delta={kpis.completionDelta}
              good={kpis.completionDelta >= 0}
            />
            <KpiCard
              title="Unassigned jobs"
              subtitle="Right now"
              value={String(kpis.unassigned)}
              delta={0}
              good={kpis.unassigned === 0}
              hideDelta
            />
          </div>

          {/* Calendar */}
          {viewRange !== 'month' ? (
            <WeekCalendar
              visibleDays={visibleDays}
              today={today}
              timeLabels={timeLabels}
              slotHeight={slotHeight}
              gridStartMinutes={gridStartMinutes}
              gridEndMinutes={gridEndMinutes}
              slotMinutes={slotMinutes}
              jobsInGrid={jobsInGrid}
              onSelectJob={setSelectedJob}
            />
          ) : (
            <MonthCalendar
              visibleDays={visibleDays}
              anchor={anchor}
              today={today}
              jobs={filteredJobs}
              onSelectJob={setSelectedJob}
            />
          )}
        </div>

        {/* RIGHT: Attention panel */}
        <aside className="rounded-2xl border border-[#E6E6E4] bg-white overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-[#E6E6E4]">
            <div className="flex gap-4 text-xs font-medium">
              {([
                { id: 'needs_action', label: 'Waiting', count: queues.needs.length },
                { id: 'upcoming', label: 'Upcoming', count: queues.upcoming.length },
                { id: 'completed', label: 'Completed', count: queues.completed.length },
              ] as const).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setRightTab(t.id)}
                  className={`relative pb-2 transition-colors ${
                    rightTab === t.id ? 'text-[#181818]' : 'text-[#6B7280] hover:text-[#111827]'
                  }`}
                >
                  {t.label}
                  <span className="ml-1 text-[10px] font-semibold text-[#6B7280]">{t.count}</span>
                  {rightTab === t.id && <span className="absolute inset-x-0 -bottom-px h-[2px] bg-[#111827]" />}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-auto p-3 space-y-2 max-h-[760px]">
            {rightTab === 'needs_action' &&
              queues.needs.map((job) => (
                <AttentionCard
                  key={job.id}
                  job={job}
                  onClick={() => setSelectedJob(job)}
                  accent="red"
                />
              ))}
            {rightTab === 'upcoming' &&
              queues.upcoming.map((job) => (
                <AttentionCard
                  key={job.id}
                  job={job}
                  onClick={() => setSelectedJob(job)}
                  accent="blue"
                />
              ))}
            {rightTab === 'completed' &&
              queues.completed.map((job) => (
                <AttentionCard
                  key={job.id}
                  job={job}
                  onClick={() => setSelectedJob(job)}
                  accent="green"
                />
              ))}
            {rightTab === 'needs_action' && queues.needs.length === 0 && (
              <p className="py-10 text-center text-[11px] text-gray-400">
                <CheckCircle className="inline h-4 w-4 mr-1 text-green-500" />
                Nothing needs attention.
              </p>
            )}
            {rightTab === 'upcoming' && queues.upcoming.length === 0 && (
              <p className="py-10 text-center text-[11px] text-gray-400">No upcoming jobs.</p>
            )}
            {rightTab === 'completed' && queues.completed.length === 0 && (
              <p className="py-10 text-center text-[11px] text-gray-400">No completed jobs.</p>
            )}
          </div>
        </aside>
      </div>

      {/* Slide-over */}
      {selectedJob && (
        <JobSlideOver job={selectedJob} onClose={() => setSelectedJob(null)} onStatusChange={handleStatusChange} />
      )}
    </div>
  );
}

// ───────────────────────── Sub-components ─────────────────────────
function KpiCard({
  title,
  subtitle,
  value,
  delta,
  good,
  hideDelta,
}: {
  title: string;
  subtitle: string;
  value: string;
  delta: number;
  good: boolean;
  hideDelta?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-[#E6E6E4] bg-white px-4 py-3 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-medium text-[#6B7280]">{title}</p>
          <p className="mt-0.5 text-[10px] text-[#9CA3AF]">{subtitle}</p>
        </div>
        {!hideDelta && (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              good ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
            }`}
          >
            {delta >= 0 ? '+' : ''}
            {delta}% vs last week
          </span>
        )}
      </div>
      <p className="mt-2 text-[26px] font-semibold text-[#111827] leading-none">{value}</p>
    </div>
  );
}

function WeekCalendar({
  visibleDays,
  today,
  timeLabels,
  slotHeight,
  gridStartMinutes,
  gridEndMinutes,
  slotMinutes,
  jobsInGrid,
  onSelectJob,
}: {
  visibleDays: Date[];
  today: Date;
  timeLabels: { key: string; mins: number; label: string }[];
  slotHeight: number;
  gridStartMinutes: number;
  gridEndMinutes: number;
  slotMinutes: number;
  jobsInGrid: { job: any; start: Date; end: Date }[];
  onSelectJob: (j: any) => void;
}) {
  return (
    <div className="rounded-2xl border border-[#E6E6E4] bg-white overflow-hidden">
      {/* Day header */}
      <div
        className="grid border-b border-[#EFEFEF] bg-white"
        style={{ gridTemplateColumns: `72px repeat(${visibleDays.length}, minmax(0,1fr))` }}
      >
        <div className="px-3 py-2 text-[11px] font-medium text-[#9CA3AF]">
          <CalendarIcon className="h-3 w-3" />
        </div>
        {visibleDays.map((d) => {
          const isToday = isSameDay(d, today);
          return (
            <div
              key={d.toISOString()}
              className={`px-3 py-2 border-l border-[#F3F4F6] ${isToday ? 'bg-[#F9FAFB]' : ''}`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-lg text-[13px] font-semibold ${
                    isToday ? 'bg-[#111827] text-white' : 'text-[#111827]'
                  }`}
                >
                  {d.getDate()}
                </span>
                <div>
                  <div className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wide">
                    {d.toLocaleDateString('en-GB', { weekday: 'short' })}
                  </div>
                  <div className="text-[10px] text-[#9CA3AF]">
                    {
                      (jobsInGrid.filter((e) => isSameDay(e.start, d)).length || 0)
                    }{' '}
                    appointment(s)
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Grid body */}
      <div className="max-h-[640px] overflow-auto">
        <div
          style={{ display: 'grid', gridTemplateColumns: `72px repeat(${visibleDays.length}, minmax(0,1fr))` }}
        >
          {/* Time labels */}
          <div className="border-r border-[#EFEFEF] bg-white">
            {timeLabels.map((t) => (
              <div
                key={t.key}
                className="border-b border-[#F3F4F6] px-2"
                style={{ height: `${slotHeight}px` }}
              >
                <div className="pt-1 text-[10px] font-medium text-[#9CA3AF]">{t.label}</div>
              </div>
            ))}
          </div>

          {visibleDays.map((d) => {
            const dayJobs = jobsInGrid.filter((e) => isSameDay(d, e.start));
            return (
              <div
                key={d.toISOString()}
                className="relative border-r border-[#F3F4F6]"
                style={{
                  height: `${timeLabels.length * slotHeight}px`,
                  backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent ${
                    slotHeight - 1
                  }px, #F3F4F6 ${slotHeight - 1}px, #F3F4F6 ${slotHeight}px)`,
                }}
              >
                {dayJobs.map((e) => {
                  const sm = e.start.getHours() * 60 + e.start.getMinutes();
                  const em = e.end.getHours() * 60 + e.end.getMinutes();
                  const cs = Math.max(gridStartMinutes, Math.min(gridEndMinutes, sm));
                  const ce = Math.max(gridStartMinutes, Math.min(gridEndMinutes, em));
                  const dur = Math.max(slotMinutes, ce - cs);
                  const top = ((cs - gridStartMinutes) / slotMinutes) * slotHeight + 2;
                  const height = (dur / slotMinutes) * slotHeight - 4;
                  if (ce <= gridStartMinutes || cs >= gridEndMinutes) return null;
                  const p = paletteForKey(e.job.assignedEngineer?.id || e.job.assignedEngineer?.name || e.job.id);
                  return (
                    <button
                      key={e.job.id}
                      type="button"
                      onClick={() => onSelectJob(e.job)}
                      className="absolute left-1 right-1 rounded-md border px-2 py-1 text-left transition hover:shadow-sm"
                      style={{
                        top,
                        height,
                        overflow: 'hidden',
                        backgroundColor: p.bg,
                        borderColor: p.border,
                      }}
                    >
                      <div className="text-[10px] font-medium" style={{ color: p.text }}>
                        {formatTime(e.start)}–{formatTime(e.end)} {e.job.client?.name || ''}
                      </div>
                      <div className="text-[11px] font-semibold truncate" style={{ color: p.text }}>
                        {e.job.title || `JOB-${String(e.job.id).slice(0, 6)}`}
                      </div>
                      <div className="text-[10px] truncate opacity-80" style={{ color: p.text }}>
                        {e.job.assignedEngineer?.name || 'Unassigned'}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MonthCalendar({
  visibleDays,
  anchor,
  today,
  jobs,
  onSelectJob,
}: {
  visibleDays: Date[];
  anchor: Date;
  today: Date;
  jobs: any[];
  onSelectJob: (j: any) => void;
}) {
  const dayKeys = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return (
    <div className="rounded-2xl border border-[#E6E6E4] bg-white overflow-hidden">
      <div className="grid grid-cols-7 border-b border-[#EFEFEF] bg-[#FAFAFA] text-[11px] font-medium text-[#6B7280]">
        {dayKeys.map((d) => (
          <div key={d} className="px-3 py-2 border-l border-[#F3F4F6] first:border-l-0">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {visibleDays.map((d) => {
          const inMonth = d.getMonth() === anchor.getMonth();
          const isToday = isSameDay(d, today);
          const dayJobs = jobs.filter((j) => j.scheduledDate && isSameDay(new Date(j.scheduledDate), d));
          return (
            <div
              key={d.toISOString()}
              className={`border-l border-b border-[#F3F4F6] min-h-[110px] p-1.5 text-[11px] ${
                inMonth ? 'bg-white' : 'bg-[#FCFCFC] text-[#9CA3AF]'
              } ${isToday ? 'bg-[#F9FAFB]' : ''}`}
            >
              <div
                className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-[12px] font-semibold ${
                  isToday ? 'bg-[#111827] text-white' : 'text-[#111827]'
                }`}
              >
                {d.getDate()}
              </div>
              <div className="mt-1 space-y-1">
                {dayJobs.slice(0, 3).map((j) => {
                  const p = paletteForKey(j.assignedEngineer?.id || j.assignedEngineer?.name || j.id);
                  return (
                    <button
                      key={j.id}
                      type="button"
                      onClick={() => onSelectJob(j)}
                      className="w-full rounded-md border px-1.5 py-0.5 text-left truncate"
                      style={{ backgroundColor: p.bg, borderColor: p.border, color: p.text }}
                    >
                      <span className="truncate font-medium">
                        {formatTime(j.scheduledDate)} {j.title || j.client?.name || 'Job'}
                      </span>
                    </button>
                  );
                })}
                {dayJobs.length > 3 && (
                  <div className="text-[10px] text-[#6B7280]">+{dayJobs.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AttentionCard({
  job,
  onClick,
  accent,
}: {
  job: any;
  onClick: () => void;
  accent: 'red' | 'blue' | 'green';
}) {
  const accentClass =
    accent === 'red'
      ? 'border-l-red-400'
      : accent === 'blue'
      ? 'border-l-indigo-400'
      : 'border-l-emerald-400';
  const p = paletteForKey(job.assignedEngineer?.id || job.assignedEngineer?.name || job.id);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-xl border border-[#E6E6E4] border-l-[3px] ${accentClass} bg-white px-3 py-3 text-left shadow-sm hover:shadow transition`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[12px] font-semibold text-[#111827] truncate">
            {job.title || `JOB-${String(job.id).slice(0, 6)}`}
          </p>
          <p className="mt-0.5 text-[11px] text-[#6B7280] truncate">{job.client?.name || '—'}</p>
        </div>
        <span
          className="inline-flex h-7 min-w-[28px] items-center justify-center rounded-full text-[10px] font-semibold"
          style={{ backgroundColor: p.bg, color: p.text }}
        >
          {(job.assignedEngineer?.name || 'U')[0]}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-1 text-[10px] text-[#6B7280]">
        {job.scheduledDate ? (
          <>
            <CalendarIcon className="h-3 w-3" />
            {formatDate(job.scheduledDate)} · {formatTime(job.scheduledDate)}
          </>
        ) : (
          <span className="italic">Not scheduled</span>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <StatusBadge status={job.status} />
        <RiskBadge flags={job.riskFlags} />
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className="mt-3 w-full rounded-md bg-[#111827] py-1.5 text-[11px] font-medium text-white hover:bg-[#2d2d2d]"
      >
        Open
      </button>
    </button>
  );
}
